import { ReactNode, useMemo, useState } from 'react';
import { Button, Pagination, Space, Table, Tag, Tooltip, notification } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/lib/table';
import { refreshPendingCounts } from 'layouts/pending-counts';
import NoteActionModal, { NoteActionConfig } from 'pages/star-shared/components/note-action-modal';
import { NoWrap } from 'pages/star-shared/components/status-tags';
import type { ApprovalKind, QueueItem } from '../contract';
import { StudioPaging, bulkApprovals, reviewMemberItem } from '../api';
import { mapStudioError } from '../studio-error';
import {
  APPROVAL_KIND_NOUN,
  EMPTY_TEXT,
  IMPACT_UNIT_LABEL,
  STUDIO_BULK_MAX,
  STUDIO_TAB_KINDS,
  flagLabel,
} from '../labels';
import { formatAgo, formatDateTime, formatKc } from '../format';
import MemberCell from './member-cell';
import styles from '../styles.module.scss';

export interface ApprovalTableProps {
  /** Loại việc đang xem (một bảng một loại, như hàng đợi sẵn có). */
  kind: ApprovalKind;
  rows: QueueItem[];
  loading?: boolean;
  /** Cột Nhóm nhảy (`team.name`, có khi gọi kèm studioId). Mặc định có. */
  showTeam?: boolean;
  /** Gọi sau mỗi thao tác thành công (tải lại bảng + số). */
  onChanged?: () => void;
  pagination?: StudioPaging;
  emptyText?: string;
}

type ReviewKind = 'LEAVE' | 'CLAIM';
const isReviewKind = (kind: ApprovalKind): kind is ReviewKind =>
  STUDIO_TAB_KINDS.includes(kind);

/**
 * Bảng đơn của thành viên (QueueItem) có ô chọn, khối 1 của tab Cần xử lý (mục 8.7.8). Chỉ LEAVE và CLAIM
 * có nút ở đây (STUDIO_TAB_KINDS): Duyệt / Từ chối bằng route sẵn có, "Duyệt đã chọn (n)" qua
 * POST /approvals/bulk (≤ 20). Từ chối luôn bắt lý do (rejectNeedsNote). OT, WAIVE, SHOP: chỉ xem.
 */
const ApprovalTable = ({
  kind,
  rows,
  loading,
  showTeam = true,
  onChanged,
  pagination,
  emptyText = EMPTY_TEXT.queueMembers,
}: ApprovalTableProps) => {
  const [action, setAction] = useState<NoteActionConfig | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const actionable = isReviewKind(kind);
  const label = APPROVAL_KIND_NOUN[kind];

  const done = () => {
    setSelectedKeys([]);
    refreshPendingCounts();
    onChanged?.();
  };

  const selectedRows = useMemo(
    () => rows.filter((r) => selectedKeys.includes(r.key) && r.canApprove),
    [rows, selectedKeys]
  );

  const openSingle = (row: QueueItem, approve: boolean) => {
    if (!isReviewKind(row.kind)) return;
    const reviewKind = row.kind;
    setAction({
      title: approve ? `Duyệt ${label}` : `Từ chối ${label}`,
      description: (
        <span>
          {row.subject?.nickName} · {row.title}
        </span>
      ),
      noteRequired: !approve,
      noteLabel: approve ? 'Ghi chú (không bắt buộc)' : 'Lý do từ chối',
      confirmText: approve ? 'Duyệt' : 'Từ chối',
      danger: !approve,
      onSubmit: async (note) => {
        try {
          await reviewMemberItem(reviewKind, row.id, approve, note);
          notification.success({ message: approve ? 'Đã duyệt.' : 'Đã từ chối.' });
          done();
        } catch (err) {
          notification.error({ message: mapStudioError(err) });
          throw err;
        }
      },
    });
  };

  const openBulk = () => {
    const ids = selectedRows.map((r) => r.id);
    if (!ids.length || ids.length > STUDIO_BULK_MAX || !actionable) return;
    setAction({
      title: `Duyệt ${ids.length} ${label} đã chọn`,
      description: 'Ghi chú (nếu có) áp dụng cho mọi đơn đã chọn. Từ chối thì làm từng đơn.',
      confirmText: `Duyệt (${ids.length})`,
      onSubmit: async (note) => {
        try {
          const res = await bulkApprovals({ kind, ids, note });
          const results = res?.results || [];
          const failed = results.filter((r) => !r.ok);
          const total = results.length || ids.length;
          const summary = `Đã duyệt ${results.length - failed.length}/${total} đơn.`;
          if (failed.length) {
            notification.warning({
              message: summary,
              description: (
                <div>
                  {failed.map((r) => (
                    <div key={r.id}>
                      #{r.id}: {r.message || mapStudioError({ message: r.code })}
                    </div>
                  ))}
                </div>
              ),
              duration: 8,
            });
          } else {
            notification.success({ message: summary });
          }
          done();
        } catch (err) {
          notification.error({ message: mapStudioError(err) });
          throw err;
        }
      },
    });
  };

  const renderActions = (row: QueueItem) => {
    if (!actionable) return null;
    const buttons: ReactNode[] = [];
    if (row.canApprove) {
      buttons.push(
        <Button
          key="approve"
          size="small"
          type="primary"
          icon={<CheckOutlined />}
          onClick={() => openSingle(row, true)}
        >
          Duyệt
        </Button>
      );
    }
    if (row.canReject) {
      buttons.push(
        <Button
          key="reject"
          size="small"
          danger
          icon={<CloseOutlined />}
          onClick={() => openSingle(row, false)}
        >
          Từ chối
        </Button>
      );
    }
    return buttons.length ? <Space size={4}>{buttons}</Space> : null;
  };

  const columns: ColumnsType<QueueItem> = [
    {
      title: 'Người đề nghị',
      key: 'subject',
      width: 210,
      render: (_, r) => (
        <MemberCell
          nickName={r.subject?.nickName}
          avatar={r.subject?.avatar}
          size={32}
          sub={r.subject?.department || null}
        />
      ),
    },
    ...(showTeam
      ? ([
          {
            title: 'Nhóm nhảy',
            key: 'team',
            width: 140,
            render: (_, r) => r.team?.name || '—',
          },
        ] as ColumnsType<QueueItem>)
      : []),
    {
      title: 'Nội dung',
      key: 'title',
      width: 280,
      render: (_, r) => (
        <div>
          <div className={styles.cellTitle}>{r.title}</div>
          {r.context?.length > 0 && (
            <ul className={styles.context}>
              {r.context.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          )}
          {r.flags?.length > 0 && (
            <span className={styles.tags}>
              {r.flags.map((code) => (
                <Tag key={code} color="gold">
                  {flagLabel(code)}
                </Tag>
              ))}
            </span>
          )}
        </div>
      ),
    },
    {
      title: 'Số lượng',
      key: 'amount',
      width: 110,
      align: 'right',
      render: (_, r) =>
        r.amount ? (
          <NoWrap>
            {formatKc(r.amount.value)} {IMPACT_UNIT_LABEL[r.amount.unit] || r.amount.unit}
          </NoWrap>
        ) : (
          '—'
        ),
    },
    {
      title: 'Chờ từ',
      key: 'createdAt',
      width: 120,
      render: (_, r) => (
        <Tooltip title={formatDateTime(r.createdAt)}>
          <NoWrap>{formatAgo(r.createdAt)}</NoWrap>
        </Tooltip>
      ),
    },
    {
      title: 'Ghi chú',
      key: 'blocked',
      width: 220,
      render: (_, r) => r.blockedReason || '—',
    },
    ...(actionable
      ? ([
          {
            title: 'Thao tác',
            key: 'actions',
            fixed: 'right',
            width: 180,
            render: (_, r) => renderActions(r),
          },
        ] as ColumnsType<QueueItem>)
      : []),
  ];

  const tooMany = selectedRows.length > STUDIO_BULK_MAX;

  return (
    <div>
      {actionable && (
        <div className={styles.bulkBar}>
          <Tooltip title={tooMany ? `Mỗi lần duyệt tối đa ${STUDIO_BULK_MAX} đơn.` : undefined}>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              disabled={!selectedRows.length || tooMany}
              onClick={openBulk}
            >
              Duyệt đã chọn ({selectedRows.length})
            </Button>
          </Tooltip>
          {selectedKeys.length > 0 && (
            <Button type="link" onClick={() => setSelectedKeys([])}>
              Bỏ chọn
            </Button>
          )}
        </div>
      )}
      <div className="custom-table">
        <Table<QueueItem>
          columns={columns}
          rowKey="key"
          dataSource={rows}
          loading={loading}
          pagination={false}
          locale={{ emptyText }}
          rowClassName={(r) => (r.blockedReason ? styles.blockedRow : '')}
          rowSelection={
            actionable
              ? {
                  selectedRowKeys: selectedKeys,
                  onChange: setSelectedKeys,
                  getCheckboxProps: (r) => ({ disabled: !r.canApprove }),
                }
              : undefined
          }
          scroll={{ x: 1300 }}
        />
        {pagination && pagination.total > pagination.pageSize && (
          <Pagination
            showSizeChanger={false}
            showLessItems
            current={pagination.page}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={pagination.onChange}
          />
        )}
      </div>
      <NoteActionModal config={action} onClose={() => setAction(null)} />
    </div>
  );
};

export default ApprovalTable;
